import { useState, useRef } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ModalBasicLayout from "../../../../Modal/ModalBasicLayout";
import ContainerModalActions from "../../../../Containers/ContainerModalActions";
import { useTranslationApp } from "../../../../../lib/i18next";
import {
  BUTTON_GO_BACK,
  INSTANT_FORM_RECOMMENDED_QUESTIONS_DESCRIPTION,
  INSTANT_FORM_RECOMMENDED_QUESTIONS_TITLE,
  INSTANT_FORM_SECTION_FORM_TYPE,
  INSTANT_FORM_SECTION_INTRO,
  INSTANT_FORM_SECTION_QUESTIONS,
  INSTANT_FORM_SECTION_END,
  INSTANT_FORM_FIELD_HEADLINE,
  INSTANT_FORM_FIELD_DESCRIPTION,
  INSTANT_FORM_FIELD_CTA,
} from "../../../../../i18n/keysTranslations";

const SectionBlock = ({ label, children }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
    <Typography
      sx={{
        fontSize: "0.7rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
      }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

const CopyButton = ({
  copyId,
  copiedId,
  onCopy,
  value,
  ariaLabel,
  iconSize = "0.75rem",
}) => {
  const isCopied = copiedId === copyId;
  return (
    <IconButton
      size="small"
      onClick={() => onCopy(value, copyId)}
      aria-label={ariaLabel}
      sx={{ p: 0.25, flexShrink: 0 }}
    >
      {isCopied ? (
        <CheckIcon sx={{ fontSize: iconSize }} />
      ) : (
        <ContentCopyIcon sx={{ fontSize: iconSize }} />
      )}
    </IconButton>
  );
};

const FieldRow = ({ label, value, onCopy, copyId, copiedId }) => (
  <Box sx={{ display: "flex", gap: 0.5, alignItems: "flex-start" }}>
    <Typography
      sx={{
        fontSize: "0.75rem",
        minWidth: 80,
        lineHeight: 1.5,
        flexShrink: 0,
      }}
    >
      {label}:
    </Typography>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        flex: 1,
        minWidth: 0,
      }}
    >
      <Typography sx={{ fontSize: "0.78rem", lineHeight: 1.5 }}>
        {value}
      </Typography>
      {value && onCopy && (
        <CopyButton
          copyId={copyId}
          copiedId={copiedId}
          onCopy={onCopy}
          value={value}
          ariaLabel="Copy"
        />
      )}
    </Box>
  </Box>
);

const ModalInstantFormRecommendedQuestions = ({
  modalOpen,
  onCloseModal,
  recommendations,
}) => {
  const { t } = useTranslationApp();

  const formType = recommendations?.formType;
  const intro = recommendations?.intro;
  const questions = recommendations?.questions || [];
  const end = recommendations?.end;

  const [copiedId, setCopiedId] = useState(null);
  const copyFeedbackTimeoutRef = useRef(null);

  const onCopyToClipboard = (value, id) => {
    if (!value) return;

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(value).catch(() => {});
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = value;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand("copy");
      } catch (e) {
        // ignore
      } finally {
        document.body.removeChild(textarea);
      }
    }

    if (copyFeedbackTimeoutRef.current)
      clearTimeout(copyFeedbackTimeoutRef.current);
    setCopiedId(id ?? "copy");
    copyFeedbackTimeoutRef.current = setTimeout(() => {
      setCopiedId(null);
      copyFeedbackTimeoutRef.current = null;
    }, 500);
  };

  if (!modalOpen) return null;

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      fullWidth={true}
      scroll="paper"
      maxWidth="sm"
      title={t(INSTANT_FORM_RECOMMENDED_QUESTIONS_TITLE)}
    >
      <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
          {t(INSTANT_FORM_RECOMMENDED_QUESTIONS_DESCRIPTION)}
        </Typography>

        {/* Tipo de formulario */}
        {formType && (
          <SectionBlock label={t(INSTANT_FORM_SECTION_FORM_TYPE)}>
            <Typography sx={{ fontSize: "0.8rem" }}>{formType}</Typography>
          </SectionBlock>
        )}

        {/* Introducción */}
        {intro && (
          <SectionBlock label={t(INSTANT_FORM_SECTION_INTRO)}>
            {intro.label && (
              <FieldRow
                label={t(INSTANT_FORM_FIELD_HEADLINE)}
                value={intro.label}
                onCopy={onCopyToClipboard}
                copyId="intro-label"
                copiedId={copiedId}
              />
            )}
            {intro.description && (
              <FieldRow
                label={t(INSTANT_FORM_FIELD_DESCRIPTION)}
                value={intro.description}
                onCopy={onCopyToClipboard}
                copyId="intro-description"
                copiedId={copiedId}
              />
            )}
          </SectionBlock>
        )}

        {/* Preguntas */}
        {questions.length > 0 && (
          <SectionBlock label={t(INSTANT_FORM_SECTION_QUESTIONS)}>
            <Box
              component="ol"
              sx={{
                pl: 3,
                m: 0,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                listStyle: "disc",
              }}
            >
              {questions.map((question, index) => (
                <Box
                  key={`${question?.label || "question"}-${index}`}
                  component="li"
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography sx={{ fontSize: "0.8rem", lineHeight: 1.4 }}>
                      {question?.label}
                    </Typography>
                    <CopyButton
                      copyId={`question-${index}`}
                      copiedId={copiedId}
                      onCopy={onCopyToClipboard}
                      value={question?.label}
                      ariaLabel="Copy question"
                    />
                  </Box>
                  {question?.type === "select" &&
                    Array.isArray(question?.options) &&
                    question.options.length > 0 && (
                      <Box
                        component="ul"
                        sx={{ pl: 2, mt: 0.5, listStyleType: "disc" }}
                      >
                        {question.options.map((option, optionIndex) => (
                          <Box
                            key={`${question?.label || "question"}-option-${optionIndex}`}
                            component="li"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <Typography sx={{ fontSize: "0.75rem" }}>
                              {option}
                            </Typography>
                            <CopyButton
                              copyId={`question-${index}-option-${optionIndex}`}
                              copiedId={copiedId}
                              onCopy={onCopyToClipboard}
                              value={option}
                              ariaLabel="Copy option"
                              iconSize="0.7rem"
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                </Box>
              ))}
            </Box>
          </SectionBlock>
        )}

        {/* Finalización */}
        {end && (
          <SectionBlock label={t(INSTANT_FORM_SECTION_END)}>
            {end.label && (
              <FieldRow
                label={t(INSTANT_FORM_FIELD_HEADLINE)}
                value={end.label}
                onCopy={onCopyToClipboard}
                copyId="end-label"
                copiedId={copiedId}
              />
            )}
            {end.description && (
              <FieldRow
                label={t(INSTANT_FORM_FIELD_DESCRIPTION)}
                value={end.description}
                onCopy={onCopyToClipboard}
                copyId="end-description"
                copiedId={copiedId}
              />
            )}
            {end.cta && (
              <FieldRow
                label={t(INSTANT_FORM_FIELD_CTA)}
                value={end.cta}
                onCopy={onCopyToClipboard}
                copyId="end-cta"
                copiedId={copiedId}
              />
            )}
          </SectionBlock>
        )}
      </Box>
      <DialogActions sx={{ p: 0 }}>
        <ContainerModalActions
          hasPadding={true}
          propsSecondaryButton={{
            showButton: true,
            onClick: onCloseModal,
            text: BUTTON_GO_BACK,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default ModalInstantFormRecommendedQuestions;
